import React, { useState, useEffect } from "react";
import { Box, Card, Typography, Grid } from "@mui/material";
import { Pie, Bar, Line } from "react-chartjs-2";

import api  from "../../utils/api";
import { processMonthlyData } from "./utils";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const softColors = [
  "#FFDDC1", "#FFABAB", "#FFC3A0", "#D4A5A5", "#85C1E9",
  "#A3E4D7", "#F9E79F", "#AED6F1", "#D7BDE2", "#D5F5E3",
];

const ViolationsTab = () => {
  const [loading, setLoading] = useState(true);
  const [violations, setViolations] = useState([]);

  useEffect(() => {
    fetchViolations();
  }, []);

  const fetchViolations = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/getViolations`);
      setViolations(response.data);
    } catch (error) {
      console.error("Error fetching violations:", error);
    } finally {
      setLoading(false);
    }
  };

  const total = violations.length;

  // 🛠 Correct Status Counts
  const closed = violations.filter(v => v.status?.toLowerCase() === "closed").length;
  const open = violations.filter(v => v.status?.toLowerCase() !== "closed").length;

  const avgClosingDays = (() => {
    const closedViolations = violations.filter(v => v.closed_at && v.violated_at);
    if (closedViolations.length === 0) return 0;
    const totalDays = closedViolations.reduce((sum, v) => {
      const created = new Date(v.violated_at);
      const closed = new Date(v.closed_at);
      return sum + (closed - created) / (1000 * 60 * 60 * 24);
    }, 0);
    return (totalDays / closedViolations.length).toFixed(1);
  })();

  const monthlyCreated = processMonthlyData(violations, "violated_at");

  const statusCounts = violations.reduce((acc, v) => {
    const status = v.status || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [{
      data: Object.values(statusCounts),
      backgroundColor: softColors,
    }],
  };

  const priorityCounts = violations.reduce((acc, v) => {
    const priority = v.priority || "Unknown";
    acc[priority] = (acc[priority] || 0) + 1;
    return acc;
  }, {});
  const priorityChartData = {
    labels: Object.keys(priorityCounts),
    datasets: [{
      data: Object.values(priorityCounts),
      backgroundColor: softColors,
    }],
  };

  const typeCounts = violations.reduce((acc, v) => {
    const type = v.violation_type || "Unknown Type";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  const topTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const topTypesChartData = {
    labels: topTypes.map(t => t[0]),
    datasets: [{
      label: "Violations",
      data: topTypes.map(t => t[1]),
      backgroundColor: softColors,
    }],
  };

  const monthlyCreatedChartData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    datasets: [{
      label: "Violations Created",
      data: monthlyCreated,
      backgroundColor: "rgba(255, 99, 132, 0.5)",
      borderColor: "rgba(255, 99, 132, 1)",
      fill: true,
    }],
  };

  return (
    <Box sx={{ paddingBottom: 5 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        🚫 Violations Overview
      </Typography>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Total Violations</Typography>
            <Typography variant="h4">{total}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Open Violations</Typography>
            <Typography variant="h4">{open}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Closed Violations</Typography>
            <Typography variant="h4">{closed}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Avg. Closing Time</Typography>
            <Typography variant="h4">{avgClosingDays} days</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Pie Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ padding: 3 }}>
            <Typography variant="h6" gutterBottom>Violations by Status</Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300, maxWidth: 400, margin: "0 auto" }}>
              {loading ? <Typography>Loading...</Typography> :
                <Pie
                  data={statusChartData}
                  options={{
                    plugins: { legend: { position: 'right' } },
                    maintainAspectRatio: false,
                  }}
                />}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ padding: 3 }}>
            <Typography variant="h6" gutterBottom>Violations by Priority</Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300, maxWidth: 400, margin: "0 auto" }}>
              {loading ? <Typography>Loading...</Typography> :
                <Pie
                  data={priorityChartData}
                  options={{
                    plugins: { legend: { position: 'right' } },
                    maintainAspectRatio: false,
                  }}
                />}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Line Chart + Bar Chart */}
      <Box sx={{ marginTop: 4 }}>
        <Card sx={{ padding: 4 }}>
          <Typography variant="h6" gutterBottom>Trends & Top Violation Types</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Violations Created per Month
              </Typography>
              {loading ? <Typography>Loading...</Typography> : <Line data={monthlyCreatedChartData} />}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Top 5 Violation Types
              </Typography>
              {loading ? <Typography>Loading...</Typography> : <Bar data={topTypesChartData} />}
            </Grid>
          </Grid>
        </Card>
      </Box>
    </Box>
  );
};

export default ViolationsTab;
